import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import AppLayout from "../components/app/AppLayout";
import { Activity, Trash2, TrendingUp, TrendingDown } from "lucide-react";

export default function CalibrationPage() {
  const { t } = useTranslation();
  const [cals, setCals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/calibrations"); setCals(data || []); }
    catch (_e) { setCals([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (machine_id, material_id) => {
    if (!window.confirm(t("calibration.resetConfirm"))) return;
    await api.delete(`/calibrations/${machine_id}|${material_id}`);
    load();
  };

  return (
    <AppLayout>
      <div className="px-8 py-8 max-w-6xl mx-auto" data-testid="calibration-page">
        <div className="label-tech mb-2">{t("calibration.label")}</div>
        <h1 className="font-display font-bold text-3xl text-slate-100 flex items-center gap-3 mb-2">
          <Activity size={26} className="text-blue-400"/> {t("calibration.title")}
        </h1>
        <p className="text-slate-400 text-sm mb-8 max-w-3xl">{t("calibration.intro")}</p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard label={t("calibration.active")} value={cals.length}/>
          <StatCard label={t("calibration.faster")} value={cals.filter((c)=>c.factor<0.95).length} accent="emerald"/>
          <StatCard label={t("calibration.slower")} value={cals.filter((c)=>c.factor>1.05).length} accent="amber"/>
          <StatCard label={t("calibration.samplesTotal")} value={cals.reduce((s,c)=>s+(c.sample_count||0),0)}/>
        </div>

        {loading ? (
          <div className="text-slate-500">{t("common.loading")}</div>
        ) : cals.length === 0 ? (
          <div className="tech-card p-16 text-center" data-testid="calibration-empty">
            <Activity size={28} className="mx-auto text-slate-500 mb-3"/>
            <div className="font-display font-medium text-slate-100">{t("calibration.emptyTitle")}</div>
            <div className="text-slate-500 text-sm mt-1 max-w-md mx-auto">{t("calibration.emptyHint")}</div>
          </div>
        ) : (
          <div className="tech-card overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-700/60 grid grid-cols-12 gap-3 text-xs label-tech">
              <div className="col-span-4">{t("calibration.machine")}</div>
              <div className="col-span-3">{t("calibration.material")}</div>
              <div className="col-span-1 text-right">{t("calibration.factor")}</div>
              <div className="col-span-2 text-right">{t("calibration.sample")}</div>
              <div className="col-span-1 text-right">{t("calibration.last")}</div>
              <div className="col-span-1"></div>
            </div>
            <div className="divide-y divide-slate-800">
              {cals.map((c) => (
                <div key={`${c.machine_id}-${c.material_id}`} className="px-5 py-3 grid grid-cols-12 gap-3 items-center" data-testid={`cal-${c.machine_id}-${c.material_id}`}>
                  <div className="col-span-4 text-sm text-slate-100">{c.machine_name}</div>
                  <div className="col-span-3 text-sm text-slate-300">{c.material_name}</div>
                  <div className={`col-span-1 text-right font-mono-data ${c.factor<0.95?"text-emerald-400":c.factor>1.05?"text-amber-400":"text-slate-300"}`}>
                    {c.factor < 0.95 ? <TrendingDown size={11} className="inline mr-1"/> : c.factor > 1.05 ? <TrendingUp size={11} className="inline mr-1"/> : null}
                    {c.factor.toFixed(2)}x
                  </div>
                  <div className="col-span-2 text-right text-xs text-slate-500 font-mono-data">{c.sample_count}</div>
                  <div className="col-span-1 text-right text-xs text-slate-500 font-mono-data">{c.last_sample_factor?.toFixed(2)}x</div>
                  <div className="col-span-1 text-right">
                    <button onClick={() => remove(c.machine_id, c.material_id)} className="p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10" data-testid={`reset-cal-${c.machine_id}-${c.material_id}`}><Trash2 size={12}/></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function StatCard({ label, value, accent }) {
  const color = accent === "emerald" ? "text-emerald-400" : accent === "amber" ? "text-amber-400" : "text-blue-400";
  return (
    <div className="tech-card p-4">
      <div className="label-tech mb-2">{label}</div>
      <div className={`font-mono-data text-3xl ${color}`}>{value}</div>
    </div>
  );
}
